// Admin → Migrations. Bringing a project over from another tracker, in six
// steps. The first two (connect, choose a project) live in the browser; once
// the admin continues, the server holds the run and does the work in the
// background, and these pages poll it.
//
// Usually a one-off job, so there is no history list: the home page shows the
// run in progress, or the last one, and the sources to start from.
import { useEffect, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  api,
  type MigrationCheck,
  type MigrationRun,
  type MigrationSource,
  type MigrationTarget,
  type Project,
  type SourceProject,
} from "../lib/api"
import { startSingleFlightPoll } from "../lib/singleFlightPoll"
import {
  formatCount,
  isBusy,
  isFinal,
  isValidKeyPrefix,
  stepForStatus,
  suggestKeyPrefix,
  targetLabel,
} from "../lib/migrations"
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  PageHeader,
  SegmentedControl,
  Select,
  Spinner,
  formatDateTime,
  useLoad,
} from "../components/ui"
import { Note, Rich, StepLayout, Stepper } from "../components/migrations/parts"
import { MappingStep } from "../components/migrations/MappingStep"
import { ImportStep, PreviewStep, ReportStep, Working } from "../components/migrations/RunSteps"

const STATUS_BADGE: Record<MigrationRun["status"], { label: string; tone: "neutral" | "accent" | "success" | "warning" | "danger" }> = {
  surveying: { label: "Reading the project", tone: "accent" },
  ready: { label: "Waiting for review", tone: "warning" },
  previewing: { label: "Preparing preview", tone: "accent" },
  previewed: { label: "Ready to start", tone: "warning" },
  queued: { label: "Starting", tone: "accent" },
  running: { label: "Importing", tone: "accent" },
  completed: { label: "Completed", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
}

function badgeFor(run: MigrationRun) {
  const b = STATUS_BADGE[run.status]
  if (run.status === "completed" && run.summary && run.summary.warnings.length + run.summary.skipped.length > 0) {
    return <Badge tone="warning">Completed with warnings</Badge>
  }
  return <Badge tone={b.tone}>{b.label}</Badge>
}

// ---------- home ----------

export function MigrationsPage() {
  const navigate = useNavigate()
  const sources = useLoad(() => api.get<{ sources: MigrationSource[] }>("/migrations/sources"))
  const current = useLoad(() => api.get<{ run: MigrationRun | null }>("/migrations/current"))
  const run = current.data?.run ?? null
  const blocking = run && !isFinal(run.status)

  return (
    <>
      <PageHeader
        title="Migrations"
        subtitle="Bring projects from another tracker into Moderniser, with their comments, attachments, time and history. Only admins can see this page."
      />

      <ErrorNote error={sources.error ?? current.error} />

      {run && (
        <section className="flex flex-col gap-2">
          <h2 className="font-semibold text-foreground">{blocking ? "Migration in progress" : "Last migration"}</h2>
          <Card className="flex flex-wrap items-center gap-x-4 gap-y-2 p-4 text-sm">
            {badgeFor(run)}
            <div className="min-w-48 flex-1">
              <p>
                <b className="font-semibold">{run.sourceProject.key}</b> <span className="text-muted">→</span>{" "}
                <b className="font-semibold">{targetLabel(run)}</b> <span className="text-muted">{run.sourceProject.name}</span>
              </p>
              <p className="text-xs text-muted">
                {run.summary
                  ? `${formatCount(run.summary.counts.tasks)} tasks, ${formatCount(run.summary.counts.comments)} comments, ${formatCount(run.summary.counts.attachments)} attachments`
                  : run.error
                    ? run.error
                    : `Step ${stepForStatus(run.status)} of 6`}
              </p>
            </div>
            <span className="text-xs text-faint">
              {formatDateTime(run.finishedAt ?? run.createdAt)} · by {run.createdByName}
            </span>
            <Button type="button" size="sm" variant={blocking ? "primary" : "ghost"} onClick={() => navigate(`/migrations/${run.id}`)}>
              {blocking ? "Continue" : "View report"}
            </Button>
          </Card>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-foreground">Start a migration</h2>
        <p className="text-sm text-muted">Pick where your projects live today. You review everything before anything is written.</p>
        {sources.loading ? (
          <Spinner />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            {(sources.data?.sources ?? []).map((source) => (
              <Card key={source.id} className={`flex flex-col gap-2.5 p-4 ${source.available ? "" : "bg-surface-subtle"}`}>
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={`flex size-8 items-center justify-center rounded-md bg-accent-soft text-[13px] font-semibold ${source.available ? "text-accent" : "text-faint"}`}
                  >
                    {source.name.slice(0, source.id === "youtrack" ? 2 : 1).toUpperCase()}
                  </span>
                  <b className={`font-semibold ${source.available ? "text-foreground" : "text-muted"}`}>{source.name}</b>
                  <span className="ml-auto">
                    {source.available ? <Badge tone="success">Available</Badge> : <Badge>Coming soon</Badge>}
                  </span>
                </div>
                <p className="text-xs text-muted">{source.description}</p>
                {source.available && (
                  <div className="flex flex-col gap-1">
                    <Button
                      type="button"
                      size="sm"
                      className="self-start"
                      disabled={!!blocking}
                      onClick={() => navigate(`/migrations/new?source=${source.id}`)}
                    >
                      Migrate from {source.name}
                    </Button>
                    {blocking && <span className="text-xs text-faint">Finish or cancel the migration in progress first.</span>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>
    </>
  )
}

function WizardHeader({ source }: { source: string }) {
  const navigate = useNavigate()
  return (
    <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
      <div>
        <p className="mb-1 text-xs text-faint">
          <Link to="/migrations" className="hover:text-foreground">
            Migrations
          </Link>{" "}
          / {source}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Migrate from {source}</h1>
        <p className="mt-1 max-w-prose text-sm text-muted">
          Six steps. Nothing is written to Moderniser until you start the import in step 5.
        </p>
      </div>
      <Button type="button" variant="subtle" onClick={() => navigate("/migrations")}>
        Close
      </Button>
    </header>
  )
}

// ---------- steps 1 and 2 ----------

export function NewMigrationPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const sourceId = params.get("source") ?? "youtrack"
  const sources = useLoad(() => api.get<{ sources: MigrationSource[] }>("/migrations/sources"))
  const source = sources.data?.sources.find((s) => s.id === sourceId)

  const [step, setStep] = useState<1 | 2>(1)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [check, setCheck] = useState<MigrationCheck | null>(null)

  if (sources.loading) return <Spinner />
  if (sources.error) return <ErrorNote error={sources.error} />
  if (!source || !source.available) {
    return <Note tone="warning">Migrating from this tracker isn't available yet.</Note>
  }

  return (
    <>
      <WizardHeader source={source.name} />
      <Stepper current={step} reachable={(n) => n === 1 && step === 2} onSelect={() => setStep(1)} />
      {step === 1 ? (
        <ConnectStep
          source={source}
          fields={fields}
          onFields={(f) => {
            setFields(f)
            setCheck(null)
          }}
          check={check}
          onChecked={setCheck}
          onContinue={() => setStep(2)}
        />
      ) : (
        <ProjectStep
          source={source}
          check={check!}
          fields={fields}
          onBack={() => setStep(1)}
          onCreated={(run) => navigate(`/migrations/${run.id}`, { replace: true })}
        />
      )}
    </>
  )
}

function ConnectStep({
  source,
  fields,
  onFields,
  check,
  onChecked,
  onContinue,
}: {
  source: MigrationSource
  fields: Record<string, string>
  onFields: (fields: Record<string, string>) => void
  check: MigrationCheck | null
  onChecked: (check: MigrationCheck) => void
  onContinue: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const complete = source.connectFields.every((f) => fields[f.name]?.trim())

  const run = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onChecked(await api.post<MigrationCheck>(`/migrations/sources/${source.id}/check`, { fields }))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <StepLayout
      title={`Connect to ${source.name}`}
      intro={`Moderniser reads your ${source.name} with a permanent token. It only reads, never changes anything there, and the token is deleted when the migration ends.`}
      guide={{
        points: [
          "Checks the address and token work.",
          "Lists the projects your account can read.",
          "Confirms people's emails are visible, so authors and assignees can be matched.",
        ],
        tip: "If people's emails are hidden, everyone is imported as a new disabled account. Ask an admin of the source to grant Read User first.",
      }}
      footer={
        <Button type="button" className="ml-auto" disabled={!check} onClick={onContinue}>
          Continue
        </Button>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={run}>
        <div className="grid gap-4 sm:grid-cols-2">
          {source.connectFields.map((f) => (
            <Field
              key={f.name}
              id={`connect-${f.name}`}
              label={f.label}
              type={f.secret ? "password" : "text"}
              autoComplete="off"
              placeholder={f.placeholder}
              hint={f.hint}
              value={fields[f.name] ?? ""}
              onChange={(e) => onFields({ ...fields, [f.name]: e.target.value })}
            />
          ))}
        </div>

        {source.tokenHelp.length > 0 && (
          <details className="rounded-md border border-border bg-surface-muted">
            <summary className="cursor-pointer px-3 py-2 text-sm text-foreground">How to create a token</summary>
            <ol className="flex list-decimal flex-col gap-1 pr-4 pb-3 pl-8 text-xs text-muted">
              {source.tokenHelp.map((line, i) => (
                <li key={i}>
                  <Rich text={line} />
                </li>
              ))}
            </ol>
          </details>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="ghost" disabled={busy || !complete}>
            {busy ? "Checking…" : check ? "Check again" : "Check connection"}
          </Button>
          {!check && !error && (
            <span className="text-xs text-muted">Check before continuing, so problems show up now and not halfway through.</span>
          )}
        </div>

        <ErrorNote error={error} />
        {check && (
          <div className="flex flex-col gap-2">
            <Note tone="success">
              Connected as <b className="font-semibold">{check.account}</b>. This token can read{" "}
              {formatCount(check.projects.length)} project{check.projects.length === 1 ? "" : "s"}
              {check.canReadEmails ? ", people's email addresses" : ""}
              {check.canReadBoards ? " and agile boards" : ""}.
            </Note>
            {!check.canReadEmails && (
              <Note tone="warning">
                People's email addresses are hidden from this token, so nobody can be matched to an existing Moderniser
                account. Ask an admin of the source to grant Read User, then check again.
              </Note>
            )}
            {!check.canReadBoards && (
              <Note tone="warning">This token can't read agile boards, so sprints won't be imported.</Note>
            )}
            {check.warnings.map((w) => (
              <Note key={w} tone="warning">
                {w}
              </Note>
            ))}
          </div>
        )}
      </form>
    </StepLayout>
  )
}

function ProjectStep({
  source,
  check,
  fields,
  onBack,
  onCreated,
}: {
  source: MigrationSource
  check: MigrationCheck
  fields: Record<string, string>
  onBack: () => void
  onCreated: (run: MigrationRun) => void
}) {
  const projects = useLoad(() => api.get<{ projects: Project[] }>("/projects"))
  const [picked, setPicked] = useState<SourceProject | null>(check.projects[0] ?? null)
  const [mode, setMode] = useState<"new" | "existing">("new")
  const [name, setName] = useState(picked?.name ?? "")
  const [key, setKey] = useState(picked ? suggestKeyPrefix(picked.key, picked.name) : "")
  const [existingId, setExistingId] = useState("")
  // Keep the source's own numbers: on by default, and only possible where no
  // key is taken yet — a new project, or an existing one with no tasks.
  const [keepNumbers, setKeepNumbers] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const choose = (p: SourceProject) => {
    setPicked(p)
    setName(p.name)
    setKey(suggestKeyPrefix(p.key, p.name))
  }

  const usable = (projects.data?.projects ?? []).filter((p) => !p.archivedAt && p.keyPrefix)
  const existing = usable.find((p) => p.id === existingId) ?? usable[0]
  const keyTaken = usable.some((p) => p.keyPrefix === key)
  const valid =
    !!picked && (mode === "new" ? name.trim().length > 0 && isValidKeyPrefix(key) && !keyTaken : !!existing)
  const existingCount = existing ? Object.values(existing.counts).reduce((a, b) => a + b, 0) : 0
  // The server has the last word (it counts archived tasks too); this only
  // stops the admin asking for something it would refuse.
  const canKeepNumbers = mode === "new" || existingCount === 0
  const keep = keepNumbers && canKeepNumbers
  const prefix = mode === "new" ? key || "KEY" : (existing?.keyPrefix ?? "KEY")
  // An example number for the label: roughly the latest issue.
  const sample = picked ? `${picked.key}-${Math.max(1, picked.issueCount)}` : ""

  const start = async () => {
    if (!picked) return
    setBusy(true)
    setError(null)
    const target: MigrationTarget =
      mode === "new"
        ? { new: { name: name.trim(), keyPrefix: key }, keepSourceNumbers: keep }
        : { projectId: existing!.id, keepSourceNumbers: keep }
    try {
      const { run } = await api.post<{ run: MigrationRun }>("/migrations", {
        source: source.id,
        fields,
        sourceProject: { id: picked.id, key: picked.key, name: picked.name },
        target,
      })
      onCreated(run)
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  return (
    <StepLayout
      title="Choose a project"
      intro={`Pick one ${source.name} project, then choose where its issues land in Moderniser. Migrate other projects one at a time.`}
      guide={{
        points: [
          "Reads the project's fields, values and people so the next step can suggest mappings.",
          "A new project is the safest choice: nothing already in Moderniser is touched.",
        ],
        tip: "Epics stay epics, their issues become tasks, and anything nested deeper becomes a sub-task — each with its own key and page.",
      }}
      footer={
        <>
          <Button type="button" variant="subtle" onClick={onBack}>
            Back
          </Button>
          <Button type="button" className="ml-auto" disabled={!valid || busy} onClick={start}>
            {busy ? "Reading the project…" : "Continue"}
          </Button>
        </>
      }
    >
      <ErrorNote error={error} />
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-semibold text-foreground">{source.name} project</legend>
        {check.projects.length === 0 ? (
          <Note tone="warning">This token can't read any projects. Check its permissions and connect again.</Note>
        ) : (
          <div className="flex max-h-80 flex-col overflow-y-auto rounded-md border border-border">
            {check.projects.map((p) => (
              <label
                key={p.id}
                className="flex cursor-pointer items-center gap-3 border-t border-border px-3 py-2.5 text-sm first:border-t-0 hover:bg-surface-subtle"
              >
                <input
                  type="radio"
                  name="source-project"
                  className="accent-accent"
                  checked={picked?.id === p.id}
                  onChange={() => choose(p)}
                />
                <span className="min-w-20 font-semibold text-foreground">{p.key}</span>
                <span className="min-w-0 truncate text-foreground">{p.name}</span>
                <span className="ml-auto shrink-0 text-xs text-muted tabular-nums">{formatCount(p.issueCount)} issues</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-foreground">Import into</span>
          <SegmentedControl
            ariaLabel="Import into"
            value={mode}
            onChange={setMode}
            options={[
              { value: "new", label: "A new project" },
              { value: "existing", label: "An existing project" },
            ]}
          />
        </div>
        {mode === "new" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="target-name"
              label="Project name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              hint={`Taken from ${source.name}. You can rename it later.`}
            />
            <div className="flex flex-col gap-1.5">
              <Field
                id="target-key"
                label="Key"
                value={key}
                maxLength={10}
                onChange={(e) => setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                hint={`2 to 10 letters, digits or _, starting with a letter. Taken from ${source.name} when it fits. The ${source.name} ID is kept on each task.`}
              />
              {key && !isValidKeyPrefix(key) && (
                <span className="text-xs text-danger">Start with a letter, and use 2 to 10 letters, digits or _.</span>
              )}
              {keyTaken && <span className="text-xs text-danger">Another project already uses {key}. Pick a different key.</span>}
            </div>
          </div>
        ) : projects.loading ? (
          <Spinner />
        ) : usable.length === 0 ? (
          <Note tone="neutral">There are no projects yet. Import into a new one instead.</Note>
        ) : (
          <div className="flex flex-col gap-3">
            <Select id="target-project" label="Project" value={existing?.id ?? ""} onChange={(e) => setExistingId(e.target.value)}>
              {usable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.keyPrefix} · {p.name}
                </option>
              ))}
            </Select>
            <Note tone="warning">
              {existingCount > 0
                ? `New tasks continue from this project's numbering, after its ${formatCount(existingCount)} tasks. `
                : "This project is empty. "}
              Existing tasks are not changed. Board statuses and types missing from the project are added to it.
            </Note>
          </div>
        )}

        <label className="flex items-start gap-2 text-sm">
          <input
            id="keep-numbers"
            type="checkbox"
            className="mt-1 accent-accent"
            checked={keep}
            disabled={!canKeepNumbers}
            onChange={(e) => setKeepNumbers(e.target.checked)}
          />
          <span className="flex flex-col gap-0.5">
            <span className={canKeepNumbers ? "text-foreground" : "text-muted"}>
              Keep {source.name}'s issue numbers{picked ? ` (${sample} becomes ${prefix}-${sample.split("-").pop()})` : ""}
            </span>
            <span className="text-xs text-muted">
              {canKeepNumbers
                ? keep
                  ? `Every epic, task and sub-task keeps its ${source.name} number, so links and references to it still point to the same issue. Numbers of drafts and deleted issues are left unused.`
                  : `Tasks are numbered ${prefix}-1, ${prefix}-2… in the order they were created.`
                : "Only possible into a new or empty project. Tasks continue from this project's numbering instead."}
            </span>
          </span>
        </label>
      </div>
    </StepLayout>
  )
}

// ---------- steps 3 to 6 ----------

export function MigrationRunPage() {
  const { id } = useParams()
  const [run, setRun] = useState<MigrationRun | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let first = true
    return startSingleFlightPoll(async () => {
      // After the first load, only keep asking while the server is working.
      if (!first && run && !isBusy(run.status)) return
      first = false
      try {
        const { run: next } = await api.get<{ run: MigrationRun }>(`/migrations/${id}`)
        setRun(next)
        setError(null)
      } catch (err) {
        setError(err)
      }
    }, 2000)
    // Restart the poll when the run moves between busy and idle, so a run the
    // admin just started is followed again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, run ? isBusy(run.status) : null])

  if (!run) return error ? <ErrorNote error={error} /> : <Spinner />

  const sourceName = run.source === "youtrack" ? "YouTrack" : run.source === "jira" ? "Jira" : "Linear"
  const step = stepForStatus(run.status)

  return (
    <>
      <WizardHeader source={sourceName} />
      <Stepper current={step} />
      <ErrorNote error={error} />
      {run.status === "surveying" && (
        <Working
          run={run}
          title="Reading the project"
          intro={`Moderniser is reading ${run.sourceProject.key} to suggest how its values map. This takes a moment for large projects.`}
        />
      )}
      {run.status === "ready" && run.suggestions && run.mapping && <MappingStep run={run} onSaved={setRun} />}
      {run.status === "previewing" && (
        <Working run={run} title="Preparing the preview" intro="Running the whole import without saving it, so the numbers are real." />
      )}
      {run.status === "previewed" && run.preview && <PreviewStep run={run} onChange={setRun} />}
      {(run.status === "queued" || run.status === "running") && <ImportStep run={run} onChange={setRun} />}
      {isFinal(run.status) && <ReportStep run={run} />}
    </>
  )
}
